import { createTheme } from '@mui/material/styles';

export const luxuryTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#af2326',
      light: '#c94144',
      dark: '#8a1a1c',
    },
    secondary: {
      main: '#ffffff',
      light: '#ffffff',
      dark: '#cccccc',
    },
    error: {
      main: '#ff5252',
      light: '#ff7b7b',
      dark: '#c50e29',
    },
    warning: {
      main: '#ffa726',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    background: {
      default: '#1c1819',
      paper: 'rgba(28, 24, 25, 0.85)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b8b8b8',
    },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", "Helvetica Neue", "Arial", sans-serif',
    h1: {
      fontSize: '3rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    },
    h2: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.01em',
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    },
    h3: {
      fontSize: '2rem',
      fontWeight: 600,
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      fontFamily: '"Plus Jakarta Sans", "Inter", sans-serif',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      letterSpacing: '0.02em',
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: 'rgba(255, 255, 255, 0.2) transparent',
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            minHeight: 24,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: 'rgba(28, 24, 25, 0.85)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          fontSize: '0.875rem',
          fontWeight: 500,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 20px rgba(175, 35, 38, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #af2326 0%, #c94144 100%)',
          boxShadow: '0 2px 10px rgba(175, 35, 38, 0.3)',
          '&:hover': {
            background: 'linear-gradient(135deg, #8a1a1c 0%, #af2326 100%)',
          },
        },
        outlined: {
          borderColor: 'rgba(175, 35, 38, 0.5)',
          color: '#af2326',
          '&:hover': {
            borderColor: '#af2326',
            backgroundColor: 'rgba(175, 35, 38, 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(28, 24, 25, 0.85)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          borderRadius: 20,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(175, 35, 38, 0.4), transparent)',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
        },
        head: {
          fontWeight: 600,
          color: '#b8b8b8',
          fontSize: '0.875rem',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
          fontSize: '0.75rem',
        },
        filled: {
          backgroundColor: 'rgba(175, 35, 38, 0.2)',
          color: '#c94144',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(175, 35, 38, 0.3)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#af2326',
            },
          },
        },
      },
    },
  },
});

export const glassStyle = {
  backgroundColor: 'rgba(28, 24, 25, 0.85)',
  backdropFilter: 'blur(16px)',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  borderRadius: '20px',
};

export const cardGlow = {
  background: 'radial-gradient(circle at top left, rgba(175,35,38,0.15), transparent)',
};

export const animationVariants = {
  fadeIn: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  },
  slideIn: {
    initial: { x: -100, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: 100, opacity: 0 },
  },
  scale: {
    initial: { scale: 0.9, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.9, opacity: 0 },
  },
  glow: {
    animate: {
      boxShadow: [
        '0 0 20px rgba(175, 35, 38, 0.3)',
        '0 0 40px rgba(175, 35, 38, 0.5)',
        '0 0 20px rgba(175, 35, 38, 0.3)',
      ],
    },
    transition: {
      duration: 2,
      repeat: Infinity,
    },
  },
};